class ElixSecureOrder extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.root = this.closest("elix-order");
    this.config = JSON.parse(
      this.querySelector("[data-member-config]").textContent,
    );
    this.s = this.config.strings;
    this.status = this.querySelector("[data-secure-status]");
    this.version = 0;
    this.submitting = false;
    this.unknownOrder = false;
    this.quote = null;
    this.csrf = null;
    this.identity = null;
    this.referral = this.querySelector("[name=elix_referral]");
    const inbound = new URL(location.href).searchParams.get("ref");
    try {
      if (inbound && /^[A-Za-z0-9]{1,50}$/.test(inbound))
        sessionStorage.setItem("elix.referral", inbound.toUpperCase());
      this.referral.value = sessionStorage.getItem("elix.referral") || "";
    } catch {
      this.referral.value = inbound || "";
    }
    this.querySelector("[data-order-login]").href =
      "/customer_authentication/login?return_to=" +
      encodeURIComponent(location.pathname + location.search);
    this.root.addEventListener("input", () => this.invalidate());
    this.root.addEventListener("change", () => this.invalidate());
    this.querySelector("[data-quote-button]").onclick = () => this.calculate();
    this.querySelector("[data-resume-order]").onclick = () => this.resume();
    this.querySelector("[data-new-order]").onclick = () => {
      this.ticket = null;
      this.unknownOrder = false;
      try {
        sessionStorage.removeItem("elix.pending.order");
      } catch {}
      this.querySelector("[data-new-order]").hidden = true;
      this.querySelector("[data-quote-button]").disabled = false;
      this.invalidate();
    };
    try {
      this.ticket = JSON.parse(sessionStorage.getItem("elix.pending.order"));
    } catch {}
    if (this.ticket) {
      this.unknownOrder = true;
      this.csrf = this.ticket.csrf;
      this.identity = this.ticket.identity;
      this.querySelector("[data-resume-order]").hidden = false;
      this.status.textContent = this.s.network_error;
    }
    this.root.updateCTA?.();
  }
  disconnectedCallback() {
    clearTimeout(this.timer);
  }
  async api(path, data) {
    const r = await fetch(this.config.proxy + path, {
      method: data === undefined ? "GET" : "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        // Identity refresh must not replay the previous account's CSRF token.
        ...(this.csrf && path !== "/me" ? { "X-Elix-CSRF": this.csrf } : {}),
      },
      ...(data === undefined
        ? {}
        : { body: JSON.stringify({ ...data, csrf: this.csrf }) }),
    });
    let value;
    try {
      value = await r.json();
    } catch {
      throw Error("service_unavailable");
    }
    if (!r.ok) {
      const e = Error(value.error || "service_unavailable");
      e.status = r.status;
      throw e;
    }
    return value;
  }
  invalidate() {
    this.version++;
    this.quote = null;
    clearTimeout(this.timer);
    this.querySelector("[data-server-quote]").hidden = true;
    this.root.updateCTA?.();
  }
  quoteValid() {
    return (
      !!this.quote &&
      this.quote.expiresAt > Date.now() &&
      !this.submitting &&
      !this.unknownOrder
    );
  }
  input() {
    const r = this.root,
      values = Object.fromEntries(new FormData(r.form)),
      country =
        r.querySelector("[name=country]")?.selectedOptions[0]?.dataset.code;
    const option = r.querySelector("[name=province]")?.selectedOptions[0];
    const address = {
      firstName: values.first_name,
      lastName: values.last_name,
      address1: values.address1,
      address2: values.address2 || "",
      city: values.city,
      zip: values.zip,
      ...(country ? { countryCode: country } : { country: values.country }),
    };
    if (values.province)
      option?.dataset.code
        ? (address.provinceCode = option.dataset.code)
        : (address.province = values.province);
    if (values.phone) address.phone = values.phone;
    if (
      r.cart.items.some(
        (i) =>
          i.selling_plan_allocation || Object.keys(i.properties || {}).length,
      )
    )
      throw Error("invalid_input");
    return {
      items: r.cart.items.map((i) => ({
        variantId: "gid://shopify/ProductVariant/" + i.variant_id,
        quantity: i.quantity,
      })),
      address,
      email: values.email,
      code: this.referral.value.trim(),
      method: values.payment,
      shippingMethod: r.shipping?.id || "standard",
      currency: r.cart.currency,
      researchAcknowledged: !!r.form.elements.research?.checked,
      existingDiscounts: [
        ...(r.cart.cart_level_discount_applications || []).map((d) => d.title),
        ...r.cart.items.flatMap((i) =>
          (i.line_level_discount_allocations || []).map(
            (d) => d.discount_application.title,
          ),
        ),
      ],
    };
  }
  async calculate() {
    if (this.submitting || this.calculating || this.unknownOrder) return;
    if (!this.root.form.reportValidity()) return;
    this.calculating = true;
    this.invalidate();
    try {
      if (this.referral.value.trim())
        sessionStorage.setItem("elix.referral", this.referral.value.trim());
      else sessionStorage.removeItem("elix.referral");
    } catch {}
    const version = this.version,
      button = this.querySelector("[data-quote-button]");
    button.disabled = true;
    this.status.textContent = this.s.loading;
    try {
      const me = await this.api("/me");
      if (version !== this.version) return;
      this.csrf = me.csrf;
      this.identity = me.authenticated ? me.profile.id : null;
      this.querySelector("[data-order-login]").hidden = me.authenticated;
      const quote = await this.api("/quote", this.input());
      if (version !== this.version) return;
      this.quote = quote;
      this.idempotency = crypto.randomUUID();
      this.status.textContent = "";
      this.querySelector("[data-server-quote]").hidden = false;
      for (const key of ["discount", "shipping", "tax", "total"])
        this.querySelector("[data-secure-" + key + "]").textContent =
          this.root.format(quote[key]);
      this.timer = setTimeout(
        () => this.invalidate(),
        Math.max(0, quote.expiresAt - Date.now()),
      );
    } catch (e) {
      this.status.textContent = this.s[e.message] || this.s.error;
    } finally {
      this.calculating = false;
      button.disabled = false;
      this.root.updateCTA();
    }
  }
  async submitOrder() {
    if (!this.quoteValid()) return;
    this.submitting = true;
    this.root.updateCTA();
    this.status.textContent = this.s.recording;
    const submitted = {
      quoteId: this.quote.id,
      idempotencyKey: this.idempotency,
    };
    this.ticket = { ...submitted, csrf: this.csrf, identity: this.identity };
    this.saveTicket();
    try {
      const me = await this.api("/me");
      const id = me.authenticated ? me.profile.id : null;
      if (id !== this.identity) {
        this.invalidate();
        throw Error("session_expired");
      }
      // Keep the quote's nonce/token for guests; replacing it changes the quote owner.
      const order = await this.api("/orders", submitted);
      this.ticket.orderId = order.id;
      this.saveTicket();
      this.unknownOrder = true;
      this.status.textContent =
        order.reference +
        " · " +
        this.s.received +
        " · " +
        this.root.format(order.total);
      this.querySelector("[data-quote-button]").disabled = true;
      await this.poll(order.id);
    } catch (e) {
      if (
        (e.status && e.status < 500 && e.message !== "idempotency_conflict") ||
        e.message === "session_expired"
      ) {
        this.invalidate();
        this.ticket = null;
        try {
          sessionStorage.removeItem("elix.pending.order");
        } catch {}
        this.status.textContent = this.s[e.message];
      } else {
        this.unknownOrder = true;
        this.querySelector("[data-resume-order]").hidden = false;
        this.status.textContent = this.s.network_error;
      }
    } finally {
      this.submitting = false;
      this.root.updateCTA();
    }
  }
  saveTicket() {
    try {
      sessionStorage.setItem("elix.pending.order", JSON.stringify(this.ticket));
    } catch {
      /* Server idempotency still applies in storage-restricted browsers. */
    }
  }
  async resume() {
    if (!this.ticket || this.submitting) return;
    this.submitting = true;
    const button = this.querySelector("[data-resume-order]");
    button.disabled = true;
    try {
      const me = await this.api("/me"),
        id = me.authenticated ? me.profile.id : null;
      if (id !== this.ticket.identity) throw Error("session_expired");
      if (!this.ticket.orderId) {
        const order = await this.api("/orders", {
          quoteId: this.ticket.quoteId,
          idempotencyKey: this.ticket.idempotencyKey,
        });
        this.ticket.orderId = order.id;
        this.saveTicket();
      }
      await this.poll(this.ticket.orderId);
    } catch (e) {
      this.status.textContent = this.s[e.message] || this.s.network_error;
      if (e.message === "quote_expired") {
        this.ticket = null;
        this.unknownOrder = false;
        try {
          sessionStorage.removeItem("elix.pending.order");
        } catch {}
        button.hidden = true;
        this.invalidate();
      }
    } finally {
      this.submitting = false;
      button.disabled = false;
      this.root.updateCTA();
    }
  }
  async poll(id) {
    for (let n = 0; n < 10; n++) {
      const order = await this.api("/receipt/" + id);
      this.status.textContent =
        order.reference +
        " · " +
        (order.status === "recorded" ? this.s.recorded : this.s.recording) +
        " · " +
        this.root.format(order.total);
      if (order.status === "recorded") {
        this.querySelector("[data-new-order]").hidden = false;
        this.querySelector("[data-resume-order]").hidden = true;
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
  }
}
if (!customElements.get("elix-secure-order"))
  customElements.define("elix-secure-order", ElixSecureOrder);
